import React from 'react';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import Authorize from '@components/Authorize';
import { ENTITY_TITLES, FORM_MODE, Permissions } from '@awp/constants';
import { Link } from 'react-router-dom';
import { ActionMenuEdit, ActionMenuView } from '@components/Common/AwpActionMenus';
import { Col, FormLabel, Row } from 'react-bootstrap';
import { FormCheckbox, FormInput, FormSelect, FormTreeSelect } from '@components/Common/AwpFormInputs';
import Table from '@components/table';
import * as Yup from 'yup';
import { isUnique } from '@awp/helpers/Utils';
import { CrudService } from '@awp/shared/CrudService';

export class HazardForecastAreasService extends CrudService {
  constructor(mode) {
    const entityName = ENTITY_TITLES.HAZARD_FORECAST_AREA;
    const entityPluralName = ENTITY_TITLES.HAZARD_FORECAST_AREAS;

    super({ entityName, entityPluralName, mode });
  }

  static mapInitialValues(entity, avalanchePrograms) {
    const avalancheProgram = avalanchePrograms?.find((program) => entity?.ParentId === program.value);

    return {
      ...(entity ?? {}),
      avalancheProgram,
    };
  }

  static mapFormToDTO(data, selectedAvalancheAreas) {
    const { avalancheProgram, ...rest } = data;

    return {
      ...rest,
      Code: rest.Code?.toUpperCase(),
      ParentId: avalancheProgram?.value,
      AvalancheAreaIds: selectedAvalancheAreas?.map((a) => a.value),
    };
  }

  static getValidations({ data, allEntities, selectedAvalancheAreas, entityName, mode }) {
    return Yup.object({
      avalancheProgram:
        mode === FORM_MODE.CREATE
          ? Yup.object().nullable().required('Associated Avalanche Program is a required field')
          : undefined,
      area:
        mode === FORM_MODE.CREATE
          ? Yup.object()
              .nullable()
              .test('area', 'Area is a required field', () => {
                return selectedAvalancheAreas?.length > 0;
              })
          : undefined,
      Name: Yup.string()
        .trim()
        .max(50, 'Maximum 50 characters are allowed')
        .required(`${entityName} Name is a required field`)
        .test('Unique Name', `${entityName} Name must be unique`, function (value) {
          return isUnique(data, allEntities, 'Name', value);
        }),
      Code: Yup.string()
        .trim()
        .max(6, 'Maximum 6 characters are allowed')
        .required('Code is a required field')
        .matches(/^[a-z0-9 ]+$/i, 'Special characters are not allowed')
        .test('Unique Code', `${entityName} Code must be unique`, function (value) {
          return isUnique(data, allEntities, 'Code', value);
        }),
    });
  }

  static getFields({
    entity,
    avalanchePrograms,
    avalancheAreas,
    readOnly,
    onAreaChange,
    onProgramChange,
    program,
    mode,
  }) {
    return (
      <>
        <Row>
          <Col className="mb-4">
            <FormSelect
              label="Avalanche Program (required)"
              id="avalancheProgram"
              name="avalancheProgram"
              readOnly={mode === FORM_MODE.VIEW || mode === FORM_MODE.EDIT}
              isMulti={false}
              options={avalanchePrograms}
              isClearable={true}
              placeholder={'Select ...'}
              postChange={(e) => {
                onProgramChange(e);
              }}
            />
          </Col>
        </Row>
        {mode === FORM_MODE.CREATE && (
          <Row>
            <Col className="mb-4">
              <FormTreeSelect
                className="show-selected-item-yes"
                readOnly={readOnly}
                disabled={!program}
                name="area"
                id="area"
                label="Area (required)"
                options={avalancheAreas}
                selectMode={'multiSelect'}
                onChange={(currentNode, selectedNodes) => {
                  onAreaChange(avalancheAreas, selectedNodes);
                }}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col className="mb-4">
            <FormInput
              label="Hazard Forecast Area Name (required)"
              name="Name"
              id="Name"
              readOnly={readOnly ? readOnly : entity?.Transmitted}
            />
          </Col>
        </Row>
        <Row>
          <Col className="mb-4">
            <FormInput
              label="Code (required)"
              id="Code"
              name="Code"
              readOnly={readOnly ? readOnly : entity?.Transmitted}
            />
          </Col>
        </Row>
        <Row>
          <Col className="mb-4">
            <FormLabel>
              <strong>Status</strong>
            </FormLabel>
            <FormCheckbox label="Active" id="IsActive" name="IsActive" readOnly={readOnly} />
          </Col>
        </Row>
        {(mode === FORM_MODE.VIEW || mode === FORM_MODE.EDIT) && (
          <Row>
            <Col className="mb-4">
              <FormLabel>
                <strong>Avalanche Areas/Sub-Areas</strong>
              </FormLabel>
              <Table
                columns={[
                  { Header: 'Name', accessor: 'Name', sortType: sortByNumericThenAlpha },
                  { Header: 'Code', accessor: 'Code', sortType: sortByNumericThenAlpha },
                ]}
                data={entity?.AvalancheAreas ?? []}
                fetchData={() => {}}
                defaultSortBy={'Code'}
                defaultDesc={false}
              />
            </Col>
          </Row>
        )}
      </>
    );
  }

  static getColumns() {
    return [
      {
        Header: 'Hazard Forecast Area Name',
        accessor: 'Name',
        sortType: sortByNumericThenAlpha,
        Cell: (c) => {
          return <div className="text-wrap">{c.value}</div>;
        },
      },
      {
        Header: 'Hazard Forecast Code',
        accessor: 'Code',
        sortType: sortByNumericThenAlpha,
        Cell: (c) => {
          return <div className="text-wrap">{c.value}</div>;
        },
      },
      {
        Header: 'Status',
        accessor: 'IsActive',
        sortType: sortByNumericThenAlpha,
        Cell: (c) => {
          if (c?.row?.original) {
            const r = c.row.original.IsActive === true ? 'Active' : 'Inactive';
            return <div className="text-wrap">{r}</div>;
          }
          return '';
        },
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        Cell: this.getActionMenu,
      },
    ];
  }

  static getActionMenu(row) {
    return (
      <>
        <div>
          <Authorize requires={[Permissions.WriteAdminTools]}>
            <Link to={`${FORM_MODE.EDIT}/${row.row.original.Id}`}>
              <ActionMenuEdit />
            </Link>
          </Authorize>
          <Link to={`${FORM_MODE.VIEW}/${row.row.original.Id}`}>
            <ActionMenuView />
          </Link>
        </div>
      </>
    );
  }
}
